import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate, unstable_useBlocker as useBlocker, useBeforeUnload } from "react-router-dom";
//========== Import components from MUI Library ============
//All Wrapper Elements
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
//All Loading related components
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
//All CSS
import { makeStyles } from "tss-react/mui";
//All Element/ Controls
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";

// All Icons
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SaveIcon from "@mui/icons-material/Save";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

//========== Import components from ANT-Design Library ============
import MenuOutlined from "@ant-design/icons/MenuOutlined";
import NodeIndexOutlined from "@ant-design/icons/NodeIndexOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import InfoCircleFilled from "@ant-design/icons/InfoCircleFilled";
import { Tooltip } from "antd";

//========== Import components from our files ============
//All Wrapper Elements
//All Sub Pages for Popup
// All Common Element/ Controls
import AddField from "./AddField";
import AddMapping from "./AddMapping";
import Template from "./Template/Template";
import UploadTemplate from "./Template/UploadTemplate";
import ErrorLog from "./Template/ErrorLog";
import DraggableTable from "../../../../Common/DraggableTable";
import { ButtonUI, ConfirmPopup } from "../../../../Common";
import NotAllowedDialog from "../../../../Common/NotAllowedDialog";
import DSPopup from "./DSPopup";
import RuleEditor from "./RuleEditor";
import Rules from "./Rules";
import CommonLayout from "../../../../Common/Layout";
import AddRelationship from "./AddReletionship";
import RelationshipTable from "./RelationshipTable";
import InfoSection from "./InfoSection";

// Others
import { getUUID, isEqual, setPrevState } from "../../../../../util/common";
import { showToast } from "../../../../../store/actions";
import DataSourceClass from "../../../../../service/dataSource";
import { checkReservedKeywords } from "../../../../../util/restrictedStatement";
import { DATA_SOURCE, OBJECT_TYPE, sampleViewTemplate, TAGS } from "../../../../../util/Data/DataModeling";
import { navEntities } from "../../../../kmk/AppData";
import HelpContextIcon from "../../../../Common/HelpPanel/HelpContextIcon";
import Tags from "../../Tags";
import { TAG_ERRORS } from "../../Tags/data";
import SmallToolbar from "../../../../Common/SmallToolbar";
import ComponentSuspense from "../../../../Common/ComponentSuspense";
import CopyButton from "../../../../Common/CopyButton";

const MyAceEditor = React.lazy(() => import("../../../../Common/AceEditor"));

const useStyles = makeStyles()({
  Header: {
    fontSize: "1.1rem",
    minHeight: "60px !important",
    backgroundColor: "#e8edf1",
    color: "black",
    marginTop: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  container: {
    padding: 0,
    backgroundColor: "#FFFFFF",
  },
  subContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    borderTop: 0,
    maxWidth: "100%",
    minWidth: "100%",
  },
  PanelContainer: {
    padding: "10px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  DataTableContainer: {
    width: "100%",
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: 5,
    overflowX: "auto",
    minHeight: "250px",
    "& .ant-table-cell": {
      backgroundColor: "#FFFFFF",
    },
    "& .MuiChip-avatarSmall": {
      width: "12px",
      height: "12px",
    },
    "& .MuiChip-filled.MuiChip-sizeSmall": {
      height: "22px",
      fontSize: "0.7125rem",
    },
  },

  TextCenter: {
    textAlign: "center !important",
  },
  TextLeft: {
    textAlign: "left !important",
  },
  DragHandle: {
    cursor: "move !important",
    color: "#000000de",
    "&:active": {
      color: "#096dd9",
    },
    "&:hover": {
      color: "#40a9ff",
    },
    "&:active,:focus,:hover": {
      outline: 0,
      textDecoration: "none",
    },
  },
  IconHolder: {
    height: "25px",
    width: "25px",
    borderRadius: "50px",
    margin: "0",
    padding: "5px 5px",
    fontSize: "19px",
  },
  breakWord: {
    wordBreak: "break-all",
  },
  deleteEntityConfirm: {
    "& span": {
      display: "block",
    },
  },
  deleteReffConfirm: {
    "& span": {
      display: "block",
    },
  },
  ImgIcon: {
    height: "1.1em",
    width: "1.1em",
    filter: "grayscale(100%)",
    marginLeft: "2px",
  },
  Alert: {
    paddingTop: 0,
    paddingBottom: 0,
    borderRadius: 20,
  },
  configArea: {
    width: "100% !important",
    border: "1px solid gray",
    fontSize: "0.7rem",
    "&::selection": { background: "#B9B9B9", color: "#000000" },
    "&::-moz-selection": { background: "#B9B9B9", color: "#000000" },
    "&::-webkit-selection": { background: "#B9B9B9", color: "#000000" },
    "& .ace_placeholder": { color: "#444" },
  },
});

export default function AddEntity() {
  const { Workspace_id, entityID } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { classes } = useStyles();

  const [entityInfoState, setEntityInfoState] = useState({
    displayName: "",
    displayNameError: "",
    loading: false,
    name: "",
    currentName: "",
    nameError: "",
    description: "",
    openPopup: false,
    openUploadPopup: false,
    openDSPopup: false,
  });
  const [attributeState, setAttributeState] = useState({
    tableDataLoading: false,
    currentEntityObj: {},
    fields: [],
    selectedFields: [],
    openAddField: false,
    openTab: "details",
    currentField: "",
    currentMapping: {},
    selectedMappings: [],
    openAddMapping: false,
    selectedRows: [],
    errors: [],
    openErrorLogPopup: false,
    templateName: "",
    templateData: [],
  });
  const [deleteState, setDeleteState] = useState({
    openConfirm: false,
    confirmTitle: "",
    confirmMessage: "",
    deleteObject: "",
    loadingForConfirm: false,
    confirmBtnName: "",
    confirmBtnColor: "",
    popupMaxWidth: "md",
  });
  const [notAllowedDialog, setNotAllowedDialog] = useState({
    openConfirm: false,
    confirmMessage: "",
    title: "",
  });
  const [relationships, setRelationships] = useState([]);
  const [showAddRelationship, setShowAddRelationship] = useState(false);
  const [loadFieldsOptions, setLoadFieldsOptions] = useState(null);
  const [doNotBlock, setDoNotBlock] = useState(false);
  const [datasources, setDatasources] = useState(false);
  const [entities, setEntities] = useState([]);
  const [sources, setSources] = useState([]);
  const [expandRules, setExpandRules] = useState(false);
  const [editRule, setEditRule] = useState({ open: false, rule: false });
  const [autoMatchingRules, setAutoMatchingRules] = useState([]);
  const [suspectMatchingRules, setSuspectMatchingRules] = useState([]);
  const [viewTemplate, setViewTemplate] = useState("");
  const [viewTemplateError, setViewTemplateError] = useState("");

  const open = Boolean(loadFieldsOptions);

  const dataTableRef = useRef();
  const templateDataRef = useRef(attributeState.templateData);
  const oldDisplayName = useRef("");
  const entityName = useRef("");
  const initialCopy = useRef({});
  const workspaceInfo = useRef({
    WorkspaceName: "",
    dataVaultEnabled: false,
    accessRole: 0,
  });
  const tagsRef = useRef(null);

  const { WorkspaceName } = workspaceInfo.current;

  const oldDisplayNameBreadcrumb = oldDisplayName.current;

  const inEditMode = useMemo(() => !!entityID, [entityID]);

  const disableAddRelationship = useMemo(
    () =>
      !entities.length ||
      (entities.length === 1 &&
        entityName.current &&
        entities[0].name.toLowerCase() === entityName.current.toLowerCase()) ||
      false,
    [entities, entityName.current]
  );

  const setOpenPopup = useCallback((popupValue) => {
    setPrevState(setEntityInfoState, { openPopup: popupValue });
  }, []);

  const setOpenUploadPopup = useCallback((popupValue) => {
    setPrevState(setEntityInfoState, { openUploadPopup: popupValue });
  }, []);

  const setOpenErrorLogPopup = useCallback((popupValue) => {
    setPrevState(setAttributeState, { openErrorLogPopup: popupValue });
  }, []);

  const setTemplateName = useCallback((name) => {
    setPrevState(setAttributeState, { templateName: name });
  }, []);

  const setErrors = useCallback((popupValue) => {
    setPrevState(setAttributeState, { errors: popupValue });
  }, []);

  const setCurrentEntityObj = useCallback((entityObj) => {
    setPrevState(setAttributeState, { currentEntityObj: entityObj });
  }, []);

  const setFields = useCallback((fieldsArr) => {
    setPrevState(setAttributeState, { fields: fieldsArr });
  }, []);

  const setOpenAddField = useCallback((openAddFieldValue) => {
    setPrevState(setAttributeState, { openAddField: openAddFieldValue });
  }, []);

  const setCurrentField = useCallback((field) => {
    setPrevState(setAttributeState, { currentField: field });
  }, []);

  const handleClick = useCallback((event) => {
    setLoadFieldsOptions(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setLoadFieldsOptions(null);
  }, []);

  const handleMenuItemClick = useCallback(
    (setState, stateVar) => {
      setPrevState(setState, { [stateVar]: true });
      handleMenuClose();
    },
    [handleMenuClose]
  );

  const navigateToDashboard = useCallback(() => {
    navigate(`/my-workspaces/${Workspace_id}/${navEntities.link}/`);
  }, [Workspace_id, navigate]);

  // Close Delete Confirm Popup
  const handleCancel = useCallback(() => {
    setPrevState(setDeleteState, { openConfirm: false, confirmBtnName: "", confirmBtnColor: "", popupMaxWidth: "md" });
  }, []);

  //Handle Adding of Mapping
  const addMapping = async (mappings) => {
    setPrevState(setAttributeState, {
      currentMapping: {
        ...attributeState.currentMapping,
        mappings,
      },
      openAddMapping: false,
    });

    const newField = attributeState.fields.find((field) => field.name === attributeState.currentMapping.fieldName);

    newField.mappings = mappings;

    if (mappings.length === 0) {
      if (autoMatchingRules.length > 0) {
        setAutoMatchingRules((prev) => {
          prev.forEach((rule) => {
            rule.groups.forEach((group) => {
              group.fields = group.fields.filter(({ id }) => newField.id !== id);
            });
            rule.groups = rule.groups.filter(({ fields }) => fields.length > 0);
          });

          const updatedData = prev.filter(({ groups = [] }) => groups.length > 0);
          return [...updatedData];
        });
      }

      if (suspectMatchingRules.length > 0) {
        setSuspectMatchingRules((prev) => {
          prev.forEach((rule) => {
            rule.groups.forEach((group) => {
              group.fields = group.fields.filter(({ id }) => newField.id !== id);
            });
            rule.groups = rule.groups.filter(({ fields }) => fields.length > 0);
          });

          const updatedData = prev.filter(({ groups = [] }) => groups.length > 0);
          return [...updatedData];
        });
      }
    }

    if (mappings.length <= 1) {
      // If there is only one mapping then remove that field from mathcing array if it exist and also empty out MDM property if it exist.
      newField.mdm = {};
    } else {
      const newlyMappedDs = mappings.map(({ dataSource }) => dataSource);
      if (Object.keys(newField.mdm).length > 0) {
        const newPriority = [];
        newField.mdm.priority.forEach((ds) => {
          if (newlyMappedDs.includes(ds)) {
            newPriority.push(ds);
          }
        });
        newField.mdm.priority = Array.from(new Set(newPriority.concat(newlyMappedDs)));
      } else {
        newField.mdm = {
          survivorship: "Source-based",
          priority: newlyMappedDs,
        };
      }
    }

    dataTableRef.current.updateRow(newField.name, newField);
  };

  //Handle Adding Field
  const addField = (value) => {
    //Checking if Updating or adding new
    if (attributeState.currentField) {
      const tField = attributeState.fields.find((field) => field.name === attributeState.currentField);
      dataTableRef.current.updateRow(attributeState.currentField, {
        ...tField,
        ...value,
      });

      setPrevState(setEntityInfoState, { currentField: value.name });
    } else {
      dataTableRef.current.addRow({
        ...value,
        mappings: [],
        mdm: {},
        dedupe: {},
        required: true,
      });
    }
  };

  // Close Delete Selected Fields
  const deleteFields = () => {
    dataTableRef.current.deleteRow(attributeState.selectedFields);
    dispatch(
      showToast({
        message: "Selected field(s) deleted successfully",
        type: "success",
      })
    );
    setPrevState(setDeleteState, { openConfirm: false, confirmBtnName: "", confirmBtnColor: "" });
  };

  // Delete current Entity and related dependencies
  const deleteEntity = async () => {
    setPrevState(setDeleteState, { loadingForConfirm: true });

    const datamodeling = new DataSourceClass();
    const { success } = await datamodeling.deleteEntityNewWay(Workspace_id, entityID);
    if (success) {
      dispatch(
        showToast({
          message: "Entity deleted successfully",
          type: "success",
        })
      );
    }

    setPrevState(setDeleteState, {
      openConfirm: false,
      loadingForConfirm: false,
      confirmBtnName: "",
      confirmBtnColor: "",
      popupMaxWidth: "md",
    });
    setDoNotBlock(true);

    if (success) setTimeout(navigateToDashboard, 10);
  };

  //Checking if configuratoins valid then saving or showing NotAllowed message
  const isValidConfig = () => {
    let invalidFields = attributeState.fields.filter((field) => checkReservedKeywords(field.name) === true) || [];
    if (!invalidFields.length) return true;
    setPrevState(setNotAllowedDialog, {
      openConfirm: true,
      title: "Reserved Keyword(s) Detected",
      confirmMessage: (
        <div style={{ width: "100%" }}>
          <span>Following attribute name(s) are reserved. Please change them to save the Entity:</span>

          <div style={{ maxHeight: "300px", overflowY: "auto", marginTop: "10px" }}>
            <List sx={{ listStyleType: "disc", pl: 4, background: "#f0f0f0" }} dense={true}>
              {invalidFields.map((field, i) => (
                <ListItem sx={{ display: "list-item", p: 0 }} key={i}>
                  <ListItemText primary={`${field.name}`} />
                </ListItem>
              ))}
            </List>
          </div>
        </div>
      ),
    });
  };

  /**
   * check if any changes in Entity screen
   * @param {Object} entityObj
   * @return {Boolean} change in Entity
   */
  const isEntityChanged = useCallback(
    (entityObj = {}) => {
      return !isEqual(initialCopy.current, entityObj);
    },
    [initialCopy.current]
  );

  /**
   * create config objects from states
   * @param {Array|undefined} fields
   * @returns {Object}
   */
  const createConfigFromState = () => {
    if (!entityInfoState.name) return {};

    let parsedTemplate = {};
    try {
      parsedTemplate = JSON.parse(viewTemplate?.trim() || "{}");
    } catch {}

    return {
      name: entityInfoState.name,
      displayName: entityInfoState.displayName.trim(),
      description: entityInfoState.description,
      fields: attributeState.fields,
      relationships,
      matchingConfig: { auto: autoMatchingRules, suspect: suspectMatchingRules },
      tags: tagsRef?.current?.getTags() || [],
      ...(inEditMode
        ? {
            id: entityID,
          }
        : {}),
      ViewTemplate: parsedTemplate,
    };
  };

  //Saving Entity
  const saveEntity = async () => {
    setPrevState(setEntityInfoState, { loading: true });
    const entity = createConfigFromState();

    let message = "";
    let redirect = true;

    const datamodeling = new DataSourceClass();
    if (inEditMode) {
      const isEntityUpdated = isEntityChanged(entity);
      if (isEntityUpdated) {
        const { success } = await datamodeling.updateEntity(Workspace_id, entityID, {
          oldName: entityName.current,
          ...entity,
        });
        if (success) message = "Entity updated successfully";
        else redirect = false;
      } else {
        redirect = false;
        message = "No changes detected, nothing to save";
      }
    } else {
      const { success } = await datamodeling.addEntity(Workspace_id, entity);
      if (success) message = "Entity added successfully";
      else redirect = false;
    }

    message !== "" &&
      dispatch(
        showToast({
          message: message,
          type: redirect ? "success" : "error",
        })
      );

    setPrevState(setEntityInfoState, { loading: false });
    if (redirect) {
      setDoNotBlock(true);
      setTimeout(navigateToDashboard, 10);
    }
  };

  //Save click
  const onSave = async () => {
    setPrevState(setEntityInfoState, { nameError: "", displayNameError: "" });

    if (entityInfoState.displayName.trim().length > 50 || entityInfoState.displayName.trim().length < 2)
      setPrevState(setEntityInfoState, { displayNameError: "Display name must be between 2 to 50 characters." });
    else if (
      (!entityName.current ||
        entityInfoState.displayName.trim().toLowerCase() !== oldDisplayName?.current?.toLowerCase()) &&
      entities.some((entity) => entity.displayName?.toLowerCase() === entityInfoState.displayName.trim().toLowerCase())
    )
      setPrevState(setEntityInfoState, { displayNameError: "Display name must be Unique." });
    else if (entityInfoState.name.length > 50 || entityInfoState.name.length < 2)
      setPrevState(setEntityInfoState, { nameError: "System name must be between 2 to 50 characters." });
    else if (checkReservedKeywords(entityInfoState.name)) {
      setPrevState(setEntityInfoState, { nameError: "Reserved keyword." });
    } else if (
      (!entityName.current || entityName.current !== entityInfoState.name) &&
      entities.some((entity) => entity.name.toLowerCase() === entityInfoState.name.toLowerCase())
    ) {
      const entity = entities.find((entity) => entity.name.toLowerCase() === entityInfoState.name.toLowerCase()).name;

      setPrevState(setEntityInfoState, { nameError: `System name must be Unique. (In use for Entity: ${entity})` });
    } else if (
      (!entityName.current || entityName.current !== entityInfoState.name) &&
      sources.some((source) => source.name.toLowerCase() === entityInfoState.name.toLowerCase())
    ) {
      const source = sources.find((source) => source.name.toLowerCase() === entityInfoState.name.toLowerCase()).name;

      setPrevState(setEntityInfoState, { nameError: `System name must be Unique. (In use for Source: ${source})` });
    } else {
      if (!isValidConfig()) return;

      const { keyLimitationError, repetitiveKeyError } = tagsRef.current.anyErrorInTags();

      if (keyLimitationError)
        return dispatch(
          showToast({
            message: TAG_ERRORS.EMTPY_KEY,
            type: "error",
          })
        );

      if (repetitiveKeyError)
        return dispatch(
          showToast({
            message: TAG_ERRORS.DUPLICATE_KEY,
            type: "error",
          })
        );

      saveEntity();
    }
  };

  const onChangeEvent = useCallback((data, pagination, filters, sorter) => {
    const { action, dataSource } = data;

    if (action === "add") {
      const id = getUUID();
      if (Array.isArray(dataSource))
        setAttributeState((prev) => ({ ...prev, fields: [...prev.fields, ...dataSource] }));
      else setAttributeState((prev) => ({ ...prev, fields: [...prev.fields, { id, ...dataSource }] }));
    } else if (action === "update") {
      const { oldName } = data;

      setAttributeState((prev) => ({
        ...prev,
        selectedFields: prev.selectedFields.map((field) => {
          return field.name === oldName ? dataSource : field;
        }),
        fields: prev.fields.map((field) => {
          return field.name === oldName ? dataSource : field;
        }),
      }));

      // Handling rename case for fields into matching rules
      if (oldName !== dataSource.name) {
        setAutoMatchingRules((prev) => {
          prev.forEach((rule) => {
            rule.groups.forEach((group) => {
              group.fields = group.fields.map((field) =>
                dataSource.id === field.id ? { ...field, name: dataSource.name } : field
              );
            });
          });

          return [...prev];
        });
        setSuspectMatchingRules((prev) => {
          prev.forEach((rule) => {
            rule.groups.forEach((group) => {
              group.fields = group.fields.map((field) =>
                dataSource.id === field.id ? { ...field, name: dataSource.name } : field
              );
            });
          });

          return [...prev];
        });
      }
    } else if (action === "delete") {
      setAttributeState((prev) => ({
        ...prev,
        selectedFields: prev.selectedFields.filter((field) => !dataSource.some((source) => source.name === field.name)),
        fields: prev.fields
          .filter((field) => !dataSource.some((source) => source.name === field.name))
          .map((field, index) => ({ ...field, key: index + 1 })),
      }));

      // Handling Matching rules based on field deletions
      setAutoMatchingRules((prev) => {
        prev.forEach((rule) => {
          rule.groups.forEach((group) => {
            group.fields = group.fields.filter(({ id }) => !dataSource.some(({ id: fID }) => id === fID));
          });
          rule.groups = rule.groups.filter(({ fields }) => fields.length > 0);
        });

        const updatedRules = prev.filter(({ groups }) => groups.length > 0);
        return [...updatedRules];
      });
      setSuspectMatchingRules((prev) => {
        prev.forEach((rule) => {
          rule.groups.forEach((group) => {
            group.fields = group.fields.filter(({ id }) => !dataSource.some(({ id: fID }) => id === fID));
          });
          rule.groups = rule.groups.filter(({ fields }) => fields.length > 0);
        });

        const updatedRules = prev.filter(({ groups }) => groups.length > 0);
        return [...updatedRules];
      });
    } else if (action === "drag") {
      setAttributeState((prev) => ({
        ...prev,
        selectedFields: [],
        fields: dataSource.map(({ key, ...other }) => other),
      }));
    } else if (action === "select") {
      const { selectedRow } = data;

      setAttributeState((prev) => ({
        ...prev,
        selectedFields: selectedRow.map(({ key, ...other }) => other),
      }));
    }

    return [];
  }, []);

  const loadFields = (selectedDSIndex) => {
    setPrevState(setEntityInfoState, { loading: true });

    // Removing mapping from that data source requierd info object
    setDatasources((prevState) => {
      prevState[selectedDSIndex].fields.forEach((field) => {
        field.entity_mapping = {};
      });
      return [...prevState];
    });

    const selectedDS = datasources[selectedDSIndex];
    let fields = JSON.parse(JSON.stringify(selectedDS.fields));

    // Removing field that is isDeleted set
    fields = fields.filter((field) => !field.hasOwnProperty("isDeleted") || !field.isDeleted);

    fields.forEach((field) => {
      if (field.type === "Reference") {
        field.type = field.referenceFrom.field.type;
        field.length = field.referenceFrom.field.length;
      }
      field.mappings = [
        {
          dataSource: selectedDS.name,
          field: field.name,
        },
      ];
      field.mdm = {};
      field.dedupe = {};
    });

    dataTableRef.current.resetData();
    setPrevState(setAttributeState, { fields: [] });

    dataTableRef.current.addRow(fields);
    setAutoMatchingRules([]);
    setSuspectMatchingRules([]);
    setPrevState(setEntityInfoState, { loading: false, openDSPopup: false });
    setPrevState(setDeleteState, { openConfirm: false });
  };

  const handleLoadClick = (selectedDataSource) => {
    const selectedDSIndex = datasources.findIndex(
      ({ name }) => name.toLowerCase() === selectedDataSource.toLowerCase()
    );

    const selectedDS = datasources[selectedDSIndex];

    let anyFieldsAlreadyMapped = selectedDS.isAnyDSFieldMapped;
    if (!anyFieldsAlreadyMapped && inEditMode) anyFieldsAlreadyMapped = attributeState.fields.length > 0;

    if (anyFieldsAlreadyMapped)
      setPrevState(setDeleteState, {
        openConfirm: true,
        confirmTitle: "Overwrite Mappings",
        confirmMessage: (
          <span className={classes.deleteReffConfirm}>
            <span>
              {`${DATA_SOURCE.singular} "${
                selectedDS.displayName
              }" is already mapped to some entity. Proceeding will overwrite the mapping between this ${DATA_SOURCE.singular.toLowerCase()} and old entity/entities.`}
            </span>
            <br />
            <span style={{ marginTop: "8px" }}>
              <span>All Matching configurations will be removed.</span>
              <br />
              {inEditMode ? `MDM set for this entity will be removed.` : ""}
              {inEditMode ? (
                <>
                  <br />
                  <br />
                </>
              ) : (
                ""
              )}
              Are you sure you want to proceed?
            </span>
          </span>
        ),
        confirmBtnName: "Load And Map",
        confirmBtnColor: "",
        handleClick: () => {
          loadFields(selectedDSIndex);
        },
        popupMaxWidth: "sm",
      });
    else loadFields(selectedDSIndex);
  };

  const handleAddRelation = (entityId) => {
    const foundRelationship = entities.find((e) => e.id === entityId);

    setRelationships((prev) => [...prev, foundRelationship.name]);
    setShowAddRelationship(false);
  };

  const checkForChanges = () => {
    if (!inEditMode || doNotBlock || workspaceInfo?.current?.accessRole === 3) return false;
    const entity = createConfigFromState();
    return isEntityChanged(entity);
  };

  const fetchDSRequiredInfo = useCallback(async () => {
    setPrevState(setEntityInfoState, { loading: true });

    const datamodeling = new DataSourceClass();
    const { success, data } = await datamodeling.getObjectRequiredInfo(Workspace_id, OBJECT_TYPE.DataSource);

    if (success) {
      const datasources = JSON.parse(data) ?? [];
      const sorted = datasources.sort((a, b) => a.name.localeCompare(b.name));
      setDatasources(sorted);
    }

    setPrevState(setEntityInfoState, { loading: false });
  }, [Workspace_id]);

  //Generating columns
  const $columns = useMemo(
    () => [
      {
        dataIndex: "drag",
        title: "",
        render: (text, record, index) => (
          <span className={classes.DragHandle}>
            <MenuOutlined />
          </span>
        ),
        className: classes.TextCenter,
        width: 46,
      },
      {
        dataIndex: "key",
        title: "Column",
        className: [classes.TextCenter],
        width: 120,
        render: (text, record) => {
          return (
            <>
              <Tooltip placement="top" title={record.description}>
                <IconButton size="small" disabled={!record.description} color="success" sx={{ mr: 1 }}>
                  <InfoCircleFilled />
                </IconButton>
              </Tooltip>
              {text}
            </>
          );
        },
      },
      {
        dataIndex: "name",
        title: "Attribute",
        className: classes.TextLeft + " " + classes.breakWord,
        width: 300,
        // searchColumn: true,
      },
      {
        dataIndex: "type",
        title: "Type",
        className: classes.TextLeft,
        width: 100,
      },
      {
        dataIndex: "length",
        title: "Length",
        className: classes.TextCenter,
        width: 100,
      },
      {
        dataIndex: "mappings",
        title: DATA_SOURCE.singular,
        className: classes.TextCenter + " " + classes.breakWord,
        width: 215,
        render: (mappings, field) => {
          if (mappings?.length) {
            return (
              <Box
                style={{
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "flex",
                }}
              >
                <Box sx={{ flex: 1, whiteSpace: "pre-line" }}>
                  {mappings.map((mapping) => `${mapping.dataSource} | ${mapping.field}`).join("\n")}
                </Box>
                <IconButton
                  className={classes.IconHolder}
                  onClick={() => {
                    setPrevState(setAttributeState, {
                      currentMapping: {
                        fieldName: field.name,
                        fieldType: field.type,
                        fieldLength: field.length,
                        mappings,
                      },
                      openAddMapping: true,
                    });
                  }}
                >
                  <EditOutlinedIcon color="primary" fontSize="inherit" />
                </IconButton>
              </Box>
            );
          } else {
            return (
              <Box
                style={{
                  display: "flex",
                }}
              >
                <Box sx={{ flex: 1 }}></Box>
                <IconButton
                  className={classes.IconHolder}
                  style={{ backgroundColor: "#C7DCFF", color: "#1A57BD" }}
                  onClick={() => {
                    setPrevState(setAttributeState, {
                      currentMapping: {
                        fieldName: field.name,
                        fieldType: field.type,
                        fieldLength: field.length,
                        mappings: [],
                      },
                      openAddMapping: true,
                    });
                  }}
                >
                  <NodeIndexOutlined fontSize="inherit" />
                </IconButton>
              </Box>
            );
          }
        },
      },
      {
        dataIndex: "mdm",
        title: "MDM",
        className: [classes.TextCenter],
        width: 45,
        render: (text, record) => {
          return record.mappings && record.mappings.length ? (
            <Chip
              label="MDM"
              size="small"
              variant="filled"
              color="info"
              icon={<img src="/images/database_chip.png" height="12px" alt="mdm" />}
              onClick={() => {
                setPrevState(setAttributeState, { currentField: record.name, openAddField: true, openTab: "MDM" });
              }}
            />
          ) : (
            ""
          );
        },
      },
      {
        dataIndex: "Action",
        title: "",
        className: classes.TextLeft,
        width: 90,
        render: (value, record, index) => {
          return (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <IconButton
                className={classes.IconHolder}
                onClick={() => {
                  setPrevState(setAttributeState, {
                    currentField: record.name,
                    openAddField: true,
                    openTab: "details",
                  });
                }}
              >
                <EditOutlinedIcon color="primary" fontSize="inherit" />
              </IconButton>
              <IconButton
                className={classes.IconHolder}
                onClick={(e) => {
                  dataTableRef.current.deleteRow([record]);
                }}
                style={{
                  marginLeft: "5px",
                  color: "#C72C2C",
                }}
              >
                <DeleteOutlineOutlinedIcon fontSize="inherit" />
              </IconButton>
            </div>
          );
        },
      },
    ],
    [classes.DragHandle, classes.IconHolder, classes.TextCenter, classes.TextLeft, classes.breakWord]
  );

  let blocker = useBlocker(() => {
    if (checkForChanges()) {
      return !window.confirm("You have unsaved changes. Are you sure you want to leave this page?");
    }
    return false;
  });

  const handleMatchingRulesAccordian = useCallback(() => {
    setExpandRules((prev) => !prev);
  }, []);

  const handleRuleEditor = useCallback((rule = false) => {
    setEditRule((prev) => ({ open: !prev.open, rule }));
  }, []);

  const handleRuleSave = useCallback(
    (data, addRule) => {
      let { name, ruleType, sources, enabled, groups, selectAll } = data;

      if (selectAll) sources = ["Any"];

      if (addRule) {
        if (ruleType === "auto")
          setAutoMatchingRules((prev) => [
            ...prev,
            { key: prev.length + 1, id: getUUID(), name, sources, enabled, groups },
          ]);
        else
          setSuspectMatchingRules((prev) => [
            ...prev,
            { key: prev.length + 1, id: getUUID(), name, sources, enabled, groups },
          ]);
      } else {
        const { id, prevRuleType } = data;
        if (prevRuleType !== ruleType) {
          if (prevRuleType === "auto") {
            setAutoMatchingRules((prev) => prev.filter((rule) => rule.id !== id));
            setSuspectMatchingRules((prev) => [...prev, { key: prev.length + 1, id, name, sources, enabled, groups }]);
          } else {
            setSuspectMatchingRules((prev) => prev.filter((rule) => rule.id !== id));
            setAutoMatchingRules((prev) => [...prev, { key: prev.length + 1, id, name, sources, enabled, groups }]);
          }
        } else {
          if (ruleType === "auto")
            setAutoMatchingRules((prev) =>
              prev.map((rule) => (rule.id === id ? { ...rule, name, sources, enabled, groups } : rule))
            );
          else
            setSuspectMatchingRules((prev) =>
              prev.map((rule) => (rule.id === id ? { ...rule, name, sources, enabled, groups } : rule))
            );
        }
      }

      handleRuleEditor(false);
    },
    [handleRuleEditor]
  );

  const clearMatchingRules = useCallback(() => {
    setAutoMatchingRules([]);
    setSuspectMatchingRules([]);
  }, []);

  const $sourceObj = useMemo(() => {
    const obj = {};
    sources.forEach(({ id, name }) => {
      obj[id] = name;
    });
    return obj;
  }, [sources]);

  const $fieldToConsiderForMatching = useMemo(
    () =>
      attributeState.fields
        .filter((field) => field.mappings.length > 0)
        .map(({ id, name, mappings }) => ({ id, name, mappings })),
    [attributeState.fields]
  );

  const $soucesToConsiderForMatching = useMemo(() => {
    const sourceSet = new Set();

    $fieldToConsiderForMatching.forEach(({ mappings }) => {
      mappings.forEach(({ dataSource }) => {
        sourceSet.add(dataSource);
      });
    });

    const finalList = [];
    sourceSet.forEach((source) => {
      const detailedSource = sources.find(({ name }) => source.toLowerCase() === name.toLowerCase());
      if (detailedSource) {
        finalList.push({
          name: detailedSource.name,
          id: detailedSource.id,
        });
      }
    });

    const source_ids = finalList.map(({ id }) => id);

    setAutoMatchingRules((prev) => {
      prev.forEach((rule) => {
        if (rule.sources && rule.sources[0] !== "Any") {
          rule.sources = rule.sources.filter((source) => source_ids.includes(source));
        }
      });

      const updatedRules = prev.filter(({ sources }) => sources.length > 0);
      return [...updatedRules];
    });

    setSuspectMatchingRules((prev) => {
      prev.forEach((rule) => {
        if (rule.sources && rule.sources[0] !== "Any") {
          rule.sources = rule.sources.filter((source) => source_ids.includes(source));
        }
      });

      const updatedRules = prev.filter(({ sources }) => sources.length > 0);
      return [...updatedRules];
    });

    return finalList;
  }, [$fieldToConsiderForMatching, sources]);

  const handleAddTag = useCallback(() => {
    tagsRef.current.handleAddTag();
  }, []);

  useBeforeUnload((event) => {
    if (checkForChanges()) {
      event.preventDefault();
      return (event.returnValue = "");
    }
  });

  useEffect(() => {
    //Load data on PageLoad
    const loadEntity = async (entityID) => {
      let loadData = {};

      setPrevState(setEntityInfoState, { loading: true });
      const datamodeling = new DataSourceClass();

      const { success, data } = await datamodeling.getObjectByID(Workspace_id, OBJECT_TYPE.Entity, entityID);
      if (!success) {
        setDoNotBlock(true);
        setTimeout(navigateToDashboard, 10);
        return;
      }
      workspaceInfo.current = {
        WorkspaceName: data.WorkspaceName,
        dataVaultEnabled: data.IsDataVaultEnabled,
        accessRole: data.accessRole,
      };

      if (entityID) {
        const { entity } = JSON.parse(data.object);

        if (!entity || Object.keys(entity).length === 0) {
          setPrevState(setEntityInfoState, { ...loadData, loading: false });
          dispatch(
            showToast({
              message: "Entity does not exists.",
              type: "error",
            })
          );
          setDoNotBlock(true);
          setTimeout(navigateToDashboard, 10);
          return;
        }
        let { entity: intialEntityObj } = JSON.parse(data.object);

        if (!entity.matchingConfig || Object.keys(entity.matchingConfig).length !== 2)
          entity.matchingConfig = { auto: [], suspect: [] };

        if (entity.relationships) setRelationships(entity.relationships);

        if (tagsRef?.current) tagsRef.current.setTagsFromParent(JSON.parse(JSON.stringify(entity?.tags || [])));

        loadData = {
          ...loadData,
          name: entity.name,
          displayName: entity.displayName,
          currentName: entity.name,
          description: entity.description,
        };
        entityName.current = entity.name;
        oldDisplayName.current = entity.displayName;

        setPrevState(setAttributeState, { fields: [], tableDataLoading: true });
        dataTableRef.current.resetData();
        if (entity.fields.length) dataTableRef.current.addRow(entity.fields);

        let { auto, suspect } = entity.matchingConfig;
        auto = auto.map((rule) => {
          rule.sources = rule.sources.map(({ id }) => id);
          return rule;
        });
        suspect = suspect.map((rule) => {
          rule.sources = rule.sources.map(({ id }) => id);
          return rule;
        });
        setAutoMatchingRules([...auto]);
        setSuspectMatchingRules([...suspect]);
        setViewTemplate(entity.ViewTemplate ? JSON.stringify(entity.ViewTemplate, null, 4) : "");

        intialEntityObj.matchingConfig = {
          auto: [...auto],
          suspect: [...suspect],
        };
        delete intialEntityObj.updatedAt;
        intialEntityObj.ViewTemplate = entity.ViewTemplate || "";
        initialCopy.current = intialEntityObj;

        setPrevState(setAttributeState, { currentEntityObj: entity, tableDataLoading: false });
      }

      setPrevState(setEntityInfoState, { ...loadData, loading: false });
    };

    const getObjects = async () => {
      const datamodeling = new DataSourceClass();
      const { success, data } = await datamodeling.getAllObjectNames(Workspace_id);
      if (!success) {
        setDoNotBlock(true);
        setTimeout(navigateToDashboard, 10);
        return;
      }

      setEntities(data.entities);
      setSources(data.sources);
    };

    const fetchIntialData = async () => await Promise.all([loadEntity(entityID), getObjects()]);

    fetchIntialData();
  }, [Workspace_id, dispatch, entityID, inEditMode, navigate, navigateToDashboard]);

  useEffect(() => {
    setAttributeState((prev) => {
      const selectedMappings = [];
      if (attributeState.fields?.length)
        attributeState.fields.forEach((field) => {
          if (field?.mappings?.length) selectedMappings.push(...field.mappings);
        });

      return {
        ...prev,
        selectedMappings,
      };
    });
  }, [attributeState.fields]);

  useEffect(() => {
    return () => {
      if (blocker.state === "blocked") blocker.reset();
    };
  }, []);

  const breadcrumbArr = useMemo(
    () => [
      { link: "/my-workspaces", displayName: "Workspaces", active: false },
      { link: `/my-workspaces/${Workspace_id}`, displayName: WorkspaceName, active: false },
      { link: `/my-workspaces/${Workspace_id}/${navEntities.link}`, displayName: "Entities", active: false },
      { link: "#", displayName: inEditMode ? oldDisplayNameBreadcrumb : "Add Entity", active: true },
    ],
    [Workspace_id, inEditMode, WorkspaceName, oldDisplayNameBreadcrumb]
  );

  const handleTemplateTextChange = (value) => {
    setViewTemplate(value);

    if (!value) {
      setViewTemplateError(false);
      return;
    }

    let hasError = false;
    try {
      JSON.parse(value);
    } catch (error) {
      console.log(error);
      hasError = true;
    }

    setViewTemplateError(hasError);
  };

  const enabledAutoRules = useMemo(
    () => autoMatchingRules.filter(({ enabled }) => enabled).length,
    [autoMatchingRules]
  );
  const enabledSuspectRules = useMemo(
    () => suspectMatchingRules.filter(({ enabled }) => enabled).length,
    [suspectMatchingRules]
  );

  return (
    <CommonLayout title="Data Modeling" breadcrumbArr={breadcrumbArr} loading={entityInfoState.loading}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {attributeState.openAddField && (
          <AddField
            fields={attributeState.fields}
            setOpenAddField={setOpenAddField}
            addField={addField}
            currentField={attributeState.currentField}
            openTab={attributeState.openTab}
            FieldSettings={{
              currentField: attributeState.currentField,
              setCurrentField,
              dataTableRef,
              setFields,
              currentEntityObj: attributeState.currentEntityObj,
              setCurrentEntityObj,
            }}
            newEntity={!inEditMode}
          />
        )}

        {!entityInfoState.loading && attributeState.openAddMapping && (
          <AddMapping
            entityName={entityName.current}
            selectedMappings={attributeState.selectedMappings}
            mappings={datasources}
            currentMapping={attributeState.currentMapping}
            addMapping={addMapping}
            fetchDSRequiredInfo={fetchDSRequiredInfo}
          />
        )}

        <Backdrop
          sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={entityInfoState.loading}
        >
          <CircularProgress color="primary" />
        </Backdrop>
        <AppBar
          sx={{
            position: "relative",
            boxShadow: 0,
            backgroundColor: "#e8edf1",
          }}
        >
          <Toolbar className={classes.Header}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography className={classes.HeaderTitle} gutterBottom component="div">
                {inEditMode ? "Edit Entity" : "Add Entity"}
              </Typography>
            </Box>
            <Box sx={{ display: "inline-flex", flex: 1 }}>
              {[1, 2].includes(workspaceInfo.current.accessRole) && (
                <ButtonUI
                  variant="contained"
                  color="success"
                  onClick={onSave}
                  disabled={
                    !entityInfoState.name ||
                    !entityInfoState.displayName ||
                    attributeState.tableDataLoading ||
                    viewTemplateError
                  }
                >
                  Save
                </ButtonUI>
              )}
              <ButtonUI style={{ marginLeft: 15 }} variant="contained" color="secondary" onClick={navigateToDashboard}>
                Cancel
              </ButtonUI>
              {inEditMode && [1, 2].includes(workspaceInfo.current.accessRole) && (
                <ButtonUI
                  style={{ marginLeft: 15 }}
                  variant="contained"
                  color="error"
                  onClick={() => {
                    setPrevState(setDeleteState, {
                      openConfirm: true,
                      confirmTitle: "Delete Entity Confirmation",
                      confirmMessage: (
                        <span className={classes.deleteEntityConfirm}>
                          <span>
                            Are you sure you want to delete “
                            {oldDisplayName.current ? oldDisplayName.current : entityName.current}”?
                          </span>
                          {relationships.length ? (
                            <>
                              <hr />
                              <span>
                                This entity has relationship with other entity/entities, <br />
                                deleting it will break the relationships with the respective entities.
                              </span>
                            </>
                          ) : (
                            ""
                          )}
                        </span>
                      ),
                      deleteObject: "Entity",
                      handleClick: deleteEntity,
                      confirmBtnName: "",
                      confirmBtnColor: "",
                      popupMaxWidth: "md",
                    });
                  }}
                  disabled={attributeState.tableDataLoading}
                >
                  Delete
                </ButtonUI>
              )}
              <Box sx={{ display: "inline-flex", alignItems: "center", marginRight: "auto" }}>
                <HelpContextIcon screen="Add Entity" />
              </Box>
              <Box className={classes.HeaderTitle} sx={{ display: "inline-flex", alignItems: "end" }}>
                <Alert
                  className={classes.Alert}
                  variant="outlined"
                  icon={<InfoOutlinedIcon fontSize="inherit" />}
                  severity="info"
                >
                  Changes require 'Apply to Data Warehouse' to take effect.
                </Alert>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          <>
            <Box className={classes.subContainer}>
              <Grid container sx={{ width: "100%" }} direction="column" justifyContent="flex-end" alignItems="left">
                <Grid className={classes.PanelContainer}>
                  <SmallToolbar>Info</SmallToolbar>
                  <InfoSection
                    displayName={entityInfoState.displayName}
                    displayNameError={entityInfoState.displayNameError}
                    currentName={entityInfoState.currentName}
                    nameError={entityInfoState.nameError}
                    dataVaultEnabled={workspaceInfo.current.dataVaultEnabled}
                    description={entityInfoState.description}
                    setEntityInfoState={setEntityInfoState}
                    setDeleteState={setDeleteState}
                    entityName={entityName.current}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box maxWidth="lg" className={classes.subContainer}>
              <Grid
                container
                sx={{ width: "100%" }}
                direction="column"
                justifyContent="flex-end"
                alignItems="left"
                spacing={0}
              >
                <Grid sx={{ padding: "20px 30px 0 30px", display: "flex", alignItems: "center" }}>
                  <SmallToolbar>Match Configuration</SmallToolbar>
                  <Box sx={{ paddingLeft: "20px" }}>
                    <ButtonUI
                      endIcon={<AddIcon />}
                      variant="contained"
                      color="info"
                      style={{ width: "150px" }}
                      disabled={$fieldToConsiderForMatching.length === 0 || $soucesToConsiderForMatching.length === 0}
                      onClick={() => handleRuleEditor(false)}
                    >
                      Add Rule
                    </ButtonUI>
                  </Box>
                  <Box sx={{ ml: "auto" }}>
                    <Alert
                      className={classes.Alert}
                      variant="outlined"
                      icon={<InfoOutlinedIcon fontSize="inherit" />}
                      severity="info"
                    >
                      Rules are combined using OR logic.
                    </Alert>
                  </Box>
                </Grid>
                <Grid className={classes.PanelContainer}>
                  <Accordion
                    expanded={expandRules && autoMatchingRules.length + suspectMatchingRules.length !== 0}
                    onChange={handleMatchingRulesAccordian}
                    disabled={autoMatchingRules.length + suspectMatchingRules.length === 0}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        Auto-Merge ({enabledAutoRules}), Suspect Match ({enabledSuspectRules})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {expandRules && autoMatchingRules.length > 0 && (
                        <Rules
                          title={"Auto-Merge"}
                          rules={autoMatchingRules}
                          setRules={setAutoMatchingRules}
                          handleRuleEditor={handleRuleEditor}
                          type="auto"
                          sourceObj={$sourceObj}
                        />
                      )}
                      {expandRules && suspectMatchingRules.length > 0 && (
                        <Rules
                          title={"Suspect Match"}
                          rules={suspectMatchingRules}
                          setRules={setSuspectMatchingRules}
                          handleRuleEditor={handleRuleEditor}
                          type="suspect"
                          sourceObj={$sourceObj}
                        />
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>
            </Box>

            <Box
              maxWidth="lg"
              className={classes.subContainer}
              sx={{ marginBottom: workspaceInfo.current.dataVaultEnabled ? "0px" : "80px" }}
            >
              <Grid container sx={{ width: "100%" }} direction="column" justifyContent="flex-end" alignItems="left">
                <Grid className={classes.PanelContainer}>
                  <SmallToolbar>Attributes</SmallToolbar>

                  <Grid container sx={{ width: "100%" }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Stack spacing={2} direction="row" sx={{ mb: "10px" }}>
                        <ButtonUI
                          endIcon={<AddIcon />}
                          variant="contained"
                          color="info"
                          style={{ width: "150px" }}
                          onClick={() => {
                            setPrevState(setAttributeState, {
                              currentField: "",
                              openAddField: true,
                              openTab: "details",
                            });
                          }}
                          disabled={attributeState.tableDataLoading}
                        >
                          Add Field
                        </ButtonUI>
                        <ButtonUI
                          variant="contained"
                          color="error"
                          style={{ marginLeft: 15 }}
                          disabled={!attributeState.selectedFields.length}
                          onClick={(e) => {
                            setPrevState(setAttributeState, { selectedRows: attributeState.selectedFields });

                            setPrevState(setDeleteState, {
                              openConfirm: true,
                              confirmTitle: "Delete Confirmation",
                              confirmMessage: `Are you sure you want to delete selected field(s)?`,
                              deleteObject: "Fields",
                              handleClick: deleteFields,
                              confirmBtnName: "",
                              confirmBtnColor: "",
                              popupMaxWidth: "md",
                            });
                          }}
                        >
                          Delete
                        </ButtonUI>
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Stack spacing={2} direction="row" sx={{ mb: "10px" }} justifyContent="flex-end">
                        {[1, 2].includes(workspaceInfo.current.accessRole) ? (
                          <>
                            <IconButton onClick={handleClick}>
                              <MoreHorizIcon fontSize="inherit" />
                            </IconButton>
                            <Menu
                              id="basic-menu"
                              anchorEl={loadFieldsOptions}
                              open={open}
                              onClose={handleMenuClose}
                              MenuListProps={{
                                "aria-labelledby": "basic-button",
                              }}
                            >
                              <MenuItem
                                onClick={() => handleMenuItemClick(setEntityInfoState, "openDSPopup")}
                                disabled={attributeState.tableDataLoading}
                              >
                                <ListItemIcon>
                                  <img
                                    src="/images/database.png"
                                    alt={DATA_SOURCE.singular}
                                    className={classes.ImgIcon}
                                  />
                                </ListItemIcon>
                                <ListItemText>{`Load From ${DATA_SOURCE.singular}`}</ListItemText>
                              </MenuItem>
                              <MenuItem
                                onClick={() => handleMenuItemClick(setEntityInfoState, "openUploadPopup")}
                                disabled={attributeState.tableDataLoading}
                              >
                                <ListItemIcon>
                                  <FileDownloadIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Load From Template</ListItemText>
                              </MenuItem>
                              <MenuItem
                                onClick={() => handleMenuItemClick(setEntityInfoState, "openPopup")}
                                disabled={attributeState.fields.length <= 0 || attributeState.tableDataLoading}
                              >
                                <ListItemIcon>
                                  <SaveIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Save Template</ListItemText>
                              </MenuItem>
                            </Menu>
                          </>
                        ) : null}
                      </Stack>
                    </Grid>
                  </Grid>
                  <Box className={classes.DataTableContainer}>
                    <DraggableTable
                      data={attributeState.fields}
                      column={$columns}
                      rowSelection={true}
                      onChange={onChangeEvent}
                      ref={dataTableRef}
                    />
                  </Box>
                </Grid>
              </Grid>
              {entityInfoState.openPopup && (
                <Template
                  Settings={{
                    open: entityInfoState.openPopup,
                    setOpen: setOpenPopup,
                    dataTableRef,
                    tagsRef,
                    popupTitle: "Save Template",
                    type: 2,
                  }}
                />
              )}
              {entityInfoState.openUploadPopup && (
                <UploadTemplate
                  setOpen={setOpenUploadPopup}
                  setFields={setFields}
                  dataTableRef={dataTableRef}
                  dataSources={datasources}
                  note="Loading fields from the template will remove all matching configuration rules. Additionally, the current tags will be overridden."
                  type={2}
                  setErrors={setErrors}
                  setOpenErrorLogPopup={setOpenErrorLogPopup}
                  setTemplateName={setTemplateName}
                  templateDataRef={templateDataRef}
                  fetchDSRequiredInfo={fetchDSRequiredInfo}
                  clearMatchingRules={clearMatchingRules}
                  tagsRef={tagsRef}
                />
              )}
              {attributeState.openErrorLogPopup && (
                <ErrorLog
                  configData={{
                    open: attributeState.openErrorLogPopup,
                    setOpenErrorLogPopup: setOpenErrorLogPopup,
                    popupTitle: "Template Error Log",
                    errorList: attributeState.errors,
                    setFields: setFields,
                    dataTableRef: dataTableRef,
                    templateDataRef: templateDataRef,
                    templateName: attributeState.templateName,
                    dataSources: datasources,
                    clearMatchingRules: clearMatchingRules,
                    tagsRef,
                  }}
                />
              )}
              {deleteState.openConfirm && (
                <ConfirmPopup
                  configData={{
                    popupTitle: deleteState.confirmTitle,
                    popupMessage: deleteState.confirmMessage,
                    handleCancel: deleteState.handleCancel || handleCancel,
                    handleClick: deleteState.handleClick,
                    loadingForConfirm: deleteState.loadingForConfirm,
                    confirmBtnName: deleteState.confirmBtnName,
                    confirmBtnColor: deleteState.confirmBtnColor,
                    maxWidth: deleteState.popupMaxWidth,
                  }}
                />
              )}
            </Box>
            {!entityInfoState.loading && entityInfoState.openDSPopup && (
              <DSPopup
                setOpen={(newState) => setPrevState(setEntityInfoState, { openDSPopup: newState })}
                title={`Load from ${DATA_SOURCE.singular}`}
                handleLoadClick={handleLoadClick}
                sources={datasources}
                fetchDSRequiredInfo={fetchDSRequiredInfo}
              />
            )}

            {workspaceInfo.current.dataVaultEnabled && (
              <Box maxWidth="lg" className={classes.subContainer}>
                <Grid container spacing={3} sx={{ padding: "10px 20px", display: "flex", width: "100%" }}>
                  <Grid size={{ sm: 12, md: 6 }}>
                    <SmallToolbar style={{ marginBottom: "5px" }}>{`${TAGS.plural}`}</SmallToolbar>
                    <ButtonUI
                      endIcon={<AddIcon />}
                      variant="contained"
                      color="info"
                      style={{ width: "150px", marginBottom: "10px" }}
                      onClick={handleAddTag}
                      disabled={entityInfoState.loading || attributeState.tableDataLoading}
                    >
                      {`Add ${TAGS.singular}`}
                    </ButtonUI>
                    <Box sx={{ width: "100%" }}>
                      <Tags ref={tagsRef} objectType={OBJECT_TYPE.Entity} />
                    </Box>
                  </Grid>
                  <Grid size={{ sm: 12, md: 6 }}>
                    <SmallToolbar style={{ marginBottom: "5px" }}>Relationships</SmallToolbar>

                    <ButtonUI
                      endIcon={<AddIcon />}
                      variant="contained"
                      color="info"
                      style={{ width: "170px", marginBottom: "10px" }}
                      onClick={() => {
                        setShowAddRelationship(true);
                      }}
                      disabled={disableAddRelationship}
                    >
                      Add Relationship
                    </ButtonUI>

                    <Box>
                      <RelationshipTable
                        relationships={relationships}
                        removeRelation={(name) => {
                          setRelationships((prev) => prev.filter((entity) => entity !== name));
                        }}
                        className={classes.DataTableContainer}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box
              maxWidth="lg"
              className={classes.subContainer}
              sx={{
                marginBottom: "80px",
              }}
            >
              <Grid container spacing={3} sx={{ padding: "10px 20px", width: "100%" }}>
                <Grid size={12}>
                  <SmallToolbar style={{ marginBottom: "5px" }}>View Template</SmallToolbar>
                  <Box className={classes.DataTableContainer} sx={{ padding: "20px !important" }}>
                    <Typography color={"#00000099"} padding={0} variant="body2" component="span">
                      Define a JSON describing Entity Profile Layout for the Data Steward. Changes to this template do
                      not require 'Apply to Data Warehouse'.
                      <br />
                      Copy the sample template <CopyButton value={sampleViewTemplate} />
                    </Typography>
                    <ComponentSuspense>
                      <MyAceEditor
                        fontSize={14}
                        mode={"json"}
                        style={
                          viewTemplateError ? { border: "1px solid red", paddingTop: "10px" } : { paddingTop: "10px" }
                        }
                        onLoad={(editorInstance) => {
                          editorInstance.container.style.resize = "both";
                          // mouseup = css resize end
                          document.addEventListener("mouseup", (e) => editorInstance.resize());
                        }}
                        label="View Template"
                        value={viewTemplate}
                        onChange={handleTemplateTextChange}
                        className={classes.configArea}
                        height="300px"
                      />
                    </ComponentSuspense>
                    {viewTemplateError && (
                      <Typography color={"error"} padding={0} variant="body2" component="span">
                        Please enter valid JSON.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </>
        </Box>
      </Box>

      {notAllowedDialog.openConfirm && (
        <NotAllowedDialog
          notAllowedDialog={notAllowedDialog}
          onClose={() => {
            setNotAllowedDialog({
              openConfirm: false,
              confirmMessage: "",
              title: "",
            });
          }}
        />
      )}
      {showAddRelationship && (
        <AddRelationship
          entities={entities.filter((entity) => entity.name !== (entityName.current || entityInfoState.name))}
          relationships={relationships}
          onClose={() => setShowAddRelationship(false)}
          onSave={handleAddRelation}
          type="auto"
        />
      )}
      {editRule.open && (
        <RuleEditor
          handleSave={handleRuleSave}
          onClose={() => handleRuleEditor(false)}
          fields={$fieldToConsiderForMatching}
          sources={$soucesToConsiderForMatching}
          rule={editRule.rule}
          rules={autoMatchingRules
            .map(({ id, name }) => ({ id, name }))
            .concat(suspectMatchingRules.map(({ id, name }) => ({ id, name })))}
        />
      )}
    </CommonLayout>
  );
}
